<script lang="ts">
export default defineComponent({
  inheritAttrs: false,
})
</script>
<script setup lang="ts">
import { getDadata } from '@caks/shared/api/user.route'
import { Component as ComponentType, defineComponent, onMounted, ref } from 'vue'

interface Props {
  startIcon?: ComponentType
  endIcon?: ComponentType
  placeholder?: string
  disabled?: boolean
  modelValue: string
  noIcons?: boolean
  label?: string
  focus?: boolean
  daData?: boolean
  cityId?: string
  type?: string
  required?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: '',
  noIcons: true,
  focus: false,
  type: 'text',
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'click-on-end-icon'): void
}>()

let timeout: ReturnType<typeof setTimeout>
const dadata = ref<string[]>([])
const isLoading = ref(false)
const isDadataOpen = ref(false)
let isFirstResultLoading = false

const onInput = (event: Event) => {
  let value = (event.target as HTMLInputElement).value

  // if(props.maxlength) {
  //   if (value.length >= props.maxlength) value = value.slice(0, props.maxlength)
  // }

  emit('update:modelValue', value)
  isLoading.value = true

  if (!isFirstResultLoading) {
    isDadataOpen.value = true
  }

  if (props.daData) {
    clearTimeout(timeout)

    timeout = setTimeout(async () => {
      dadata.value = []
      const { data } = await getDadata(props.cityId as string, value)

      dadata.value = [...new Set(data)].filter((el) => !!el)
      isLoading.value = false

      isFirstResultLoading = !(value.trim() === '')
    }, 500)
  }
}

const focus = () => {
  input.value?.focus()
}

onMounted(() => {
  if (props.focus) {
    input.value?.focus()
  }
})

const isFocused = ref(false)
const input = ref<HTMLInputElement | undefined>(undefined)
const onDadataClick = (value: string) => {
  emit('update:modelValue', value)
  dadata.value = []
  isDadataOpen.value = false
  isFirstResultLoading = false
}

defineExpose({
  focus,
})
</script>

<template>
  <div class="wrapper">
    <label v-if="label">{{ label }}</label>
    <div
      class="input__wrapper"
      :class="{ input__wrapper_focus: isFocused, input__required: required }"
    >
      <div v-if="!noIcons" class="input__icon input__icon_start">
        <component :is="startIcon" />
      </div>
      <input
        :value="modelValue"
        @focusin="isFocused = true"
        @focusout="isFocused = false"
        @input="onInput"
        ref="input"
        :type="type ? type : 'text'"
        class="input"
        :placeholder="placeholder"
        v-bind="$attrs"
        :disabled="disabled"
        :required="required"
      />
      <div v-if="!noIcons" class="input__icon input__icon_end" @click="emit('click-on-end-icon')">
        <component :is="endIcon" />
      </div>
    </div>
    <transition name="fade">
      <div v-if="daData && modelValue && isDadataOpen" class="wrapper__da-data da-data">
        <p v-if="isLoading" class="da-data__empty">
          <span class="loader"></span>
        </p>
        <ul v-else-if="dadata.length" class="da-data__list">
          <li v-for="data in dadata" @click="onDadataClick(data)">{{ data }}</li>
        </ul>
        <p v-else class="da-data__empty">Ничего не найдено</p>
      </div>
    </transition>
  </div>
</template>

<style scoped lang="scss">
.wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  position: relative;

  label {
    @include h5-2;
    color: $sub-text;
  }

  .da-data {
    height: auto;
    max-height: 200px;
    width: 100%;
    background-color: $white;
    overflow: auto;

    position: absolute;
    top: 54px;
    border-radius: 8px;
    box-shadow: 0 0 8px rgba(0, 0, 0, 0.35);
    z-index: 50;

    &::-webkit-scrollbar {
      width: 10px;
    }

    &::-webkit-scrollbar-track {
      background: $ui-gray-2;
      border-radius: 10px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: $sub-text;
      border-radius: 10px;
      border: 2px solid $ui-gray-2;
    }

    &__list {
      display: flex;
      flex-direction: column;
      width: 100%;

      li {
        padding: 8px;
        @include h4-8;
        color: $primary-text;
        cursor: pointer;

        &:hover {
          color: $primary-green;
        }
      }
    }

    &__empty {
      display: flex;
      align-items: center;
      justify-content: center;
      @include h4-8;
      color: $primary-text;

      height: 200px;
    }
  }
}

.input {
  &__wrapper {
    padding: 0 16px;
    width: 100%;
    display: flex;
    align-items: center;
    gap: 6px;
    border: 1.5px solid $ui-gray-1;
    background-color: $white;
    @extend %hover-transition;
    border-radius: 8px;

    &_focus {
      background-color: $sub-green;
      border-color: $primary-green;
    }
  }

  &__required {
    position: relative;

    &:after {
      content: '*';
      position: absolute;
      top: 5px;
      right: 5px;
      @include h5-2;
    }
  }

  width: 100%;

  @include h5-2;
  color: $primary-text;
  background-color: $white;

  height: 50px;

  @extend %hover-transition;

  &::placeholder {
    @include h5-2;
    color: $sub-text;
    opacity: 1;
  }
  &:focus {
    background-color: $sub-green;
  }

  &__icon {
    &:deep(> svg) {
      width: 16px;
      height: 16px;
    }

    &_end {
      cursor: pointer;
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.loader {
  width: 48px;
  height: 48px;
  border: 5px solid $primary-green;
  border-bottom-color: transparent;
  border-radius: 50%;
  display: inline-block;
  box-sizing: border-box;
  animation: rotation 1s linear infinite;
}

@keyframes rotation {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
